/**
 * MED-1346: staff record a no-show or late change the system did not see,
 * for example a participant who told the coach by email. The claim is paid
 * at once, within the cap of two paid misses on each 1:1.
 */

import { useEffect, useState } from 'react';
import {
  Modal,
  Stack,
  Select,
  Group,
  Button,
  Text,
  Alert,
} from '@mantine/core';
import { AlertCircle } from 'react-feather';
import { DateTimeField } from '../../../../components/common';
import { missClaimsApi } from '../../../../services/api/missClaims';
import { getApiErrorMessage } from '../../../../services/api/client';
import {
  utcToTimezoneDate,
  timezoneDateToUtc,
  INTERNAL_TIMEZONE,
} from '../../../../utils/timezone';
import { missKindLabel } from '../../../../components/coaches/missClaimText';
import { showMissResult } from '../../../../components/coaches/missClaimQuestion';
import { DEFAULT_START_TIME_ON_DATE_PICK } from '../../../../utils/sessionEndTime';
import type { MissClaimKind } from '../../../../types';

const KIND_OPTIONS: { value: MissClaimKind; label: string }[] = [
  { value: 'late_cancel', label: missKindLabel('late_cancel') },
  { value: 'late_reschedule', label: missKindLabel('late_reschedule') },
  { value: 'no_show', label: missKindLabel('no_show') },
];

interface FlagLateChangeModalProps {
  opened: boolean;
  onClose: () => void;
  cohortId: string;
  sessionId: string;
  participantName: string;
  /** Prefills the original time. */
  scheduledStart: string | null;
  onFlagged: () => void;
}

export function FlagLateChangeModal({
  opened,
  onClose,
  cohortId,
  sessionId,
  participantName,
  scheduledStart,
  onFlagged,
}: FlagLateChangeModalProps) {
  const [kind, setKind] = useState<MissClaimKind>('late_cancel');
  const [originalStart, setOriginalStart] = useState<Date | null>(null);
  const [changeAt, setChangeAt] = useState<Date | null>(null);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // The pickers hold ET wall-clock times, as in the Edit Session modal.
  useEffect(() => {
    if (!opened) return;
    setKind('late_cancel');
    setOriginalStart(
      scheduledStart
        ? utcToTimezoneDate(new Date(scheduledStart), INTERNAL_TIMEZONE)
        : null
    );
    setChangeAt(null);
    setError(null);
  }, [opened, scheduledStart]);

  // F4: an old error no longer applies once the user changes an input.
  const edit =
    <T,>(set: (value: T) => void) =>
    (value: T) => {
      set(value);
      setError(null);
    };

  // MED-1346 QA (comment 13101): a change, or the start of a no-show, cannot be
  // later than now. The pickers hold ET wall-clock times, so "now" is ET too.
  const nowEt = utcToTimezoneDate(new Date(), INTERNAL_TIMEZONE) ?? new Date();

  // A no-show has no separate change time: the participant missed the start.
  const changeTime = kind === 'no_show' ? originalStart : changeAt;
  // maxDate blocks later days only, so a later time today is caught here.
  const inFuture = !!changeTime && changeTime.getTime() > nowEt.getTime();
  const futureError = inFuture
    ? kind === 'no_show'
      ? 'A no-show cannot be for a session that has not started.'
      : 'The time of the change cannot be in the future.'
    : undefined;
  const canSave = !!originalStart && !!changeTime && !inFuture && !saving;

  const handleSave = async () => {
    if (!originalStart || !changeTime) return;
    setSaving(true);
    setError(null);
    try {
      const { claim } = await missClaimsApi.flag(cohortId, sessionId, {
        kind,
        originalStart: timezoneDateToUtc(
          originalStart,
          INTERNAL_TIMEZONE
        )!.toISOString(),
        // The API takes no change time for a no-show.
        ...(kind === 'no_show'
          ? {}
          : {
              changeAt: timezoneDateToUtc(
                changeTime,
                INTERNAL_TIMEZONE
              )!.toISOString(),
            }),
      });
      // F4: the pay result comes from the server, never from a guess before the save.
      showMissResult(
        `${missKindLabel(claim.kind)} flagged`,
        claim.decision === 'paid'
          ? 'Medley pays the coach for this session. The payment shows in Payments Due.'
          : 'Medley will not pay for this session. Medley pays for two missed sessions on each 1:1 at most.'
      );
      onFlagged();
      onClose();
    } catch (err) {
      setError(getApiErrorMessage(err, 'Could not flag the late change'));
    } finally {
      setSaving(false);
    }
  };

  return (
    <Modal opened={opened} onClose={onClose} title="Flag late change" size="md">
      <Stack gap="md">
        <Text size="sm" c="dimmed">
          Record a no-show or a change with less than 24 hours' notice for{' '}
          {participantName}.
        </Text>
        <Select
          label="What happened"
          data={KIND_OPTIONS}
          value={kind}
          onChange={value => {
            if (!value) return;
            setKind(value as MissClaimKind);
            setError(null);
          }}
          allowDeselect={false}
          required
        />
        <DateTimeField
          label="Original session time (ET)"
          value={originalStart}
          onChange={edit(setOriginalStart)}
          maxDate={kind === 'no_show' ? nowEt : undefined}
          error={kind === 'no_show' ? futureError : undefined}
          defaultTimeOnDatePick={DEFAULT_START_TIME_ON_DATE_PICK}
          required
        />
        {kind !== 'no_show' && (
          <DateTimeField
            label="Time of the change (ET)"
            value={changeAt}
            onChange={edit(setChangeAt)}
            defaultDate={originalStart ?? undefined}
            maxDate={nowEt}
            error={futureError}
            defaultTimeOnDatePick={DEFAULT_START_TIME_ON_DATE_PICK}
            required
          />
        )}
        {error && (
          <Alert icon={<AlertCircle size={16} />} color="red" variant="light">
            {error}
          </Alert>
        )}
        <Group justify="flex-end">
          <Button variant="default" onClick={onClose} disabled={saving}>
            Cancel
          </Button>
          <Button onClick={handleSave} loading={saving} disabled={!canSave}>
            Flag late change
          </Button>
        </Group>
      </Stack>
    </Modal>
  );
}
